import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { GetLanguageString } from "../helper/Components";
import Draggable from "react-draggable";
import { LoadingSpinner, applyClassToCharacters, decodeHtmlEntities } from "../../constants/const";
import { ApiFinance } from "../funds/ApiFinance";
import { ApiDocuments } from "../documents/ApiDocuments";
import { getFileNameWithoutExtension } from "../helper/Method";
import ShareTransferContextPanel from "../funds/ShareTransferContextPanel";
import swal from "sweetalert";
import { useTranslation } from "react-i18next";

const formatDisplayDate = (dateVal) => {
  if (!dateVal) return "-";
  if (typeof dateVal === "string") {
    const cleanStr = dateVal.split("T")[0].trim();
    const parts = cleanStr.split(/[-/]/);
    if (parts.length === 3) {
      if (parts[0].length === 4) {
        return `${parts[2].padStart(2, "0")}/${parts[1].padStart(2, "0")}/${parts[0]}`;
      } else if (parts[2].length === 4) {
        return `${parts[0].padStart(2, "0")}/${parts[1].padStart(2, "0")}/${parts[2]}`;
      }
    }
  }
  const d = new Date(dateVal);
  return isNaN(d.getTime()) ? String(dateVal) : d.toLocaleDateString("en-GB");
};

const formatDisplayAmount = (amountVal, entries) => {
  const numAmt = parseFloat(String(amountVal || 0).replace(/,/g, ""));
  if (numAmt > 0) return new Intl.NumberFormat().format(numAmt);
  if (Array.isArray(entries) && entries.length > 0) {
    const sum = entries.reduce((acc, e) => acc + (parseFloat(String(e.amount || 0).replace(/,/g, "")) || 0), 0);
    if (sum > 0) return new Intl.NumberFormat().format(sum);
  }
  return "0";
};

const isAssetDonationNotification = (code) =>
  (code || "").toLowerCase() === "req_asset_donation";

const isDeleteNotification = (code) => {
  const c = (code || "").toLowerCase();
  return c === "req_delete_halqa_share" || c === "req_delete_markaz_share";
};

const isExpenseNotification = (code) => {
  const c = (code || "").toLowerCase();
  return (
    c === "req_expense_from_amanat" ||
    c === "req_edit_expense_from_amanat" ||
    c === "req_expense_from_cash_in_hand" ||
    c === "req_edit_expense_from_cash_in_hand" ||
    c === "req_halqa_share" ||
    c === "req_edit_halqa_share" ||
    c === "req_delete_halqa_share" ||
    c === "req_markaz_share" ||
    c === "req_edit_markaz_share" ||
    c === "req_delete_markaz_share"
  );
};

const entryDetailsTitleKey = (code) => {
  if (isAssetDonationNotification(code)) return "finance_asset_details";
  if (isExpenseNotification(code)) return "finance_other_expense_entry_details";
  return "finance_other_income_entry_details";
};

const entryHeadLabelKey = (code) => {
  if (isAssetDonationNotification(code)) return "finance_asset_head";
  if (isExpenseNotification(code)) return "funds_expense_head";
  return "funds_income_head";
};

const entrySubHeadLabelKey = (code) => {
  if (isAssetDonationNotification(code)) return "finance_asset";
  if (isExpenseNotification(code)) return "funds_expense_sub_head";
  return "funds_income_sub_head";
};

//#region styles (Pattern matched to reference)
const cardStyle = (borderColor) => ({
  borderRadius: "10px",
  border: "1px solid #E5E7EB",
  borderLeft: `4px solid ${borderColor}`,
  background: "#FFFFFF",
  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
  marginBottom: "10px",
});

const labelStyle = {
  fontSize: "10.5px",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "#9CA3AF",
  marginBottom: "2px",
};

const valueStyle = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#1F2937",
  lineHeight: "1.3",
};

const sectionHeaderStyle = (color) => ({
  fontSize: "12px",
  fontWeight: 700,
  letterSpacing: "0.05em",
  color: color,
  textTransform: "uppercase",
  marginBottom: "10px",
  display: "flex",
  alignItems: "center",
  gap: "6px",
});

const animatedBody = {
  animation: "fadeSlideIn 0.25s ease-out",
  padding: "14px 18px",
};

const amountBadgeStyle = {
  display: "inline-block",
  padding: "2px 10px",
  borderRadius: "20px",
  backgroundColor: "#EFF6FF",
  color: "#2563EB",
  fontWeight: 700,
  fontSize: "13px",
  border: "1px solid #BFDBFE",
};

const amountBadgeProposedStyle = {
  display: "inline-block",
  padding: "2px 10px",
  borderRadius: "20px",
  backgroundColor: "#ECFDF5",
  color: "#059669",
  fontWeight: 700,
  fontSize: "13px",
  border: "1px solid #A7F3D0",
};

const itemBoxStyle = (isModified) => ({
  backgroundColor: isModified ? "#ECFDF5" : "#F9FAFB",
  border: isModified ? "1px solid #A7F3D0" : "1px solid #F3F4F6",
  borderRadius: "8px",
  padding: "8px 12px",
  minHeight: "64px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
});
const btnStyle = {
  borderRadius: "8px",
  padding: "6px 14px",
  fontWeight: 600,
  fontSize: "12px",
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
};
//#endregion

const FundsOtherIncomeNotificationDetails = ({
  setViewOtherIncomeNotificationDetails,
  notificationId,
}) => {
  const { t } = useTranslation();
  const [notificationresponse, setNotificationresponse] = useState({});
  const [loading, setLoading] = useState(false);
  const [viewButtonDisable, setViewButtonDisable] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);
  const toggle = () => setViewOtherIncomeNotificationDetails(false);

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const handleDrag = (e, ui) =>
    setPosition((p) => ({ x: p.x + ui.deltaX, y: p.y + ui.deltaY }));
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [shareContext, setShareContext] = useState(null);
  const [shareContextLoading, setShareContextLoading] = useState(false);

  useEffect(() => {
    if (!notificationId) return;
    setLoading(true);
    ApiFinance()
      .GetOtherIncomeDataForNotification(notificationId)
      .then((res) => {
        if (res.data) setNotificationresponse(res.data || {});
      })
      .finally(() => setLoading(false));
  }, [notificationId]);

  const openFile = async (fileUrlParam) => {
    setViewButtonDisable(true);
    try {
      const isApproved = notificationresponse.isApproved || notificationresponse.is_approved;
      const transactionId = notificationresponse.transactionId || notificationresponse.transaction_id;
      const targetUrl = fileUrlParam || notificationresponse.file || notificationresponse.File;
      if (isApproved && transactionId) {
        const res = await ApiFinance().ViewTransactionAttachment(transactionId);
        const contentType = (res.headers && (res.headers["content-type"] || res.headers["Content-Type"])) || "application/octet-stream";
        const blob = new Blob([res.data], { type: contentType });
        const objUrl = window.URL.createObjectURL(blob);
        window.open(objUrl, "_blank");
        setTimeout(() => window.URL.revokeObjectURL(objUrl), 100);
      } else if (targetUrl) {
        window.open(targetUrl, "_blank");
      }
    } catch (err) {
      swal({ title: t("error_opening_attachment") || "Error opening file", icon: "error", buttons: "OK" });
    } finally {
      setViewButtonDisable(false);
    }
  };

  const downloadFile = async (driveFileId) => {
    setButtonDisable(true);
    try {
      const isApproved = notificationresponse.isApproved || notificationresponse.is_approved;
      const transactionId = notificationresponse.transactionId || notificationresponse.transaction_id;
      const targetDriveId = driveFileId || notificationresponse.fileDriveId || notificationresponse.file_drive_id;
      if (isApproved && transactionId) {
        const res = await ApiFinance().ViewTransactionAttachment(transactionId);
        const contentType = (res.headers && (res.headers["content-type"] || res.headers["Content-Type"])) || "application/octet-stream";
        const blob = new Blob([res.data], { type: contentType });
        const objUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objUrl;
        a.download = `LoanFromMarkaz_${transactionId}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => window.URL.revokeObjectURL(objUrl), 100);
      } else if (targetDriveId) {
        const response = await ApiDocuments().downloadDocument(targetDriveId);
        if (response.status === 200) {
          window.location.href = `${response.data.downloadLink}&confirm=t`;
        } else {
          swal({ title: t(response.data), icon: "error", buttons: "OK" });
        }
      } else if (notificationresponse.file || notificationresponse.File) {
        window.open(notificationresponse.file || notificationresponse.File, "_blank");
      }
    } catch (error) {
      swal({ title: error.message, icon: "error", buttons: "OK" });
    } finally {
      setButtonDisable(false);
    }
  };

  const handleViewAttachmentFileId = (fileId) => {
    ApiFinance()
      .ViewAttachmentByFileId(fileId)
      .then((res) => {
        const file = new Blob([res.data], { type: res.headers["content-type"] || "application/pdf" });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL, "_blank");
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleDownloadAttachmentFileId = (fileId, index) => {
    ApiFinance()
      .ViewAttachmentByFileId(fileId)
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `attachment_${index + 1}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch((err) => {
        console.error(err);
      });
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeSlideIn {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .inmodal .modal-content {
            border-radius: 16px !important;
            border: none !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
            overflow: hidden;
          }
          .inmodal .modal-header {
            background-color: #F9FAFB !important;
            border-bottom: 1px solid #F3F4F6 !important;
            padding: 16px 24px !important;
          }
          .inmodal .modal-footer {
            background-color: #F9FAFB !important;
            border-top: 1px solid #F3F4F6 !important;
            padding: 16px 24px !important;
          }
          .inmodal .modal-title {
            font-weight: 700 !important;
            font-size: 16px !important;
            color: #111827 !important;
            display: flex;
            align-items: center;
            gap: 8px;
          }
        `}
      </style>

      <Draggable position={position} onDrag={handleDrag} cancel=".no-drag">
        <Modal size="xl" isOpen className="inmodal">
          <ModalHeader className="modal-title">
            <i className="fa fa-info-circle text-primary"></i>
            <span>
              <GetLanguageString props="courses_report_details" />
            </span>
          </ModalHeader>

          {loading ? (
            <ModalBody className="text-center py-4">
              <LoadingSpinner />
            </ModalBody>
          ) : (
            <ModalBody style={animatedBody}>
              {(() => {
                const notificationCode = (notificationresponse.notificationCode || "").toLowerCase();
                const isMarkazShareNotification =
                  notificationCode === "req_markaz_share" ||
                  notificationCode === "req_edit_markaz_share";
                const calcAmountNum = parseFloat(
                  notificationresponse.calculated_amount || notificationresponse.calculatedAmount || 0
                );
                const propAmountNum = parseFloat(
                  (notificationresponse.amount || notificationresponse.transferAmount || "0")
                    .toString()
                    .replace(/,/g, "")
                );
                const isHalqaDeficit =
                  !isMarkazShareNotification &&
                  calcAmountNum > 0 &&
                  propAmountNum > 0 &&
                  propAmountNum < calcAmountNum;
                const isDeficit = isMarkazShareNotification
                  ? !!(notificationresponse.is_below_due_share || notificationresponse.isBelowDueShare)
                  : isHalqaDeficit;
                const deficitText = isMarkazShareNotification
                  ? t("markaz_share_deficit_notification", {
                      amount: propAmountNum.toLocaleString(),
                      due: calcAmountNum.toLocaleString(),
                    })
                  : t("halqa_share_deficit_notification", {
                      amount: propAmountNum.toLocaleString(),
                      due: calcAmountNum.toLocaleString(),
                    });

                const loadMoreDetails = () => {
                  if (showMoreDetails) {
                    setShowMoreDetails(false);
                    return;
                  }
                  setShowMoreDetails(true);
                  if (shareContext || !notificationId) return;
                  setShareContextLoading(true);
                  ApiFinance()
                    .GetMarkazShareNotificationContext(notificationId)
                    .then((res) => setShareContext(res.data || {}))
                    .catch(() => setShareContext(null))
                    .finally(() => setShareContextLoading(false));
                };

                const isDelete = isDeleteNotification(notificationCode);
                const isMarkazShareDelete = (notificationCode || "").toLowerCase() === "req_delete_markaz_share";

                return (
                  <>
                    {isDelete && (
                      <div
                        className="no-drag"
                        style={{
                          backgroundColor: "#FEF2F2",
                          border: "1px solid #EF4444",
                          borderRadius: "8px",
                          padding: "14px 18px",
                          marginBottom: "16px",
                          color: "#991B1B",
                          boxShadow: "0 1px 3px rgba(239, 68, 68, 0.1)",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                          <i className="fa fa-exclamation-circle text-danger" style={{ fontSize: "18px" }}></i>
                          <strong style={{ fontSize: "15px" }}>
                            <GetLanguageString props={isMarkazShareDelete ? "markaz_share_deletion_notice" : "halqa_share_deletion_notice"} />
                          </strong>
                        </div>
                        <div style={{ fontSize: "13px", color: "#7F1D1D", marginTop: "4px" }}>
                          <strong><GetLanguageString props="deletion_reason" />: </strong>
                          <span>{notificationresponse.memo || notificationresponse.original?.memo || "-"}</span>
                        </div>
                      </div>
                    )}
                    {isDeficit && (
                      <div
                        className="no-drag"
                        style={{
                          backgroundColor: "#FEF3C7",
                          border: "1px solid #F59E0B",
                          borderRadius: "8px",
                          padding: "12px 16px",
                          marginBottom: "16px",
                          color: "#92400E",
                          fontWeight: "600",
                          fontSize: "14px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "12px",
                        }}
                      >
                        <span>
                          <i className="fa fa-exclamation-triangle" style={{ marginRight: "8px", color: "#D97706" }}></i>
                          {deficitText}
                        </span>
                        {isMarkazShareNotification && (
                          <Button color="warning" size="sm" onClick={loadMoreDetails}>
                            <GetLanguageString props="markaz_share_more_details" />
                          </Button>
                        )}
                      </div>
                    )}
                    {isMarkazShareNotification && isDeficit && showMoreDetails && (
                      <div className="no-drag" style={{ marginBottom: "16px" }}>
                        <ShareTransferContextPanel
                          context={shareContext}
                          loading={shareContextLoading}
                          minimumLabelKey="share_minimum_markaz"
                        />
                      </div>
                    )}
                  </>
                );
              })()}
              {notificationresponse.original ? (
                (() => {
                  const origAmountStr = formatDisplayAmount(
                    notificationresponse.original.amount || notificationresponse.original.transferAmount,
                    notificationresponse.original.entries
                  );
                  const propAmountStr = formatDisplayAmount(
                    notificationresponse.amount || notificationresponse.transferAmount,
                    notificationresponse.entries
                  );
                  const isAmountChanged = origAmountStr !== propAmountStr;

                  const origDateStr = formatDisplayDate(notificationresponse.original.date || notificationresponse.original.requestDate);
                  const propDateStr = formatDisplayDate(notificationresponse.date || notificationresponse.requestDate);
                  const isDateChanged = origDateStr !== propDateStr;

                  const origMemoStr = (notificationresponse.original.memo || "").trim();
                  const propMemoStr = (notificationresponse.memo || "").trim();
                  const isMemoChanged = origMemoStr !== propMemoStr;

                  const accountHead = decodeHtmlEntities(
                    notificationresponse.original?.accountHead || notificationresponse.accountHead
                  );
                  const dimension2Name = decodeHtmlEntities(
                    notificationresponse.original?.dimension2Name || notificationresponse.dimension2Name
                  );
                  const bankAccountName = notificationresponse.original?.bankAccountName || notificationresponse.bankAccountName;

                  let calcAmountNum = parseFloat(notificationresponse.calculated_amount || notificationresponse.calculatedAmount || 0);
                  const origAmountNum = parseFloat(
                    (notificationresponse.original?.amount || notificationresponse.original?.transferAmount || "0").replace(/,/g, "")
                  );
                  const isHalqaShareNotification = (accountHead || "").toLowerCase().includes("halqa share") ||
                    (notificationresponse.notificationCode || "").toLowerCase().includes("halqa_share");

                  if (calcAmountNum === 0 && isHalqaShareNotification && origAmountNum > 0) {
                    calcAmountNum = origAmountNum;
                  }

                  const isDelete = isDeleteNotification(notificationresponse.notificationCode);

                  const topDetails = [];
                  if (accountHead) topDetails.push({
                    labelKey: notificationresponse.notificationCode === "req_asset_donation"
                      ? "finance_asset_category"
                      : "funds_account",
                    value: accountHead
                  });
                  if (dimension2Name) topDetails.push({
                    labelKey: notificationresponse.notificationCode === "req_asset_donation"
                      ? "finance_asset"
                      : "funds_dimension_2",
                    value: dimension2Name
                  });
                  const isMarkazShareNotification =
                    (notificationresponse.notificationCode || "").toLowerCase().includes("markaz_share");

                  if (bankAccountName && !isMarkazShareNotification) topDetails.push({ labelKey: "fund_bank_account", value: bankAccountName });
                  if (isDelete || !isAmountChanged) topDetails.push({
                    labelKey: notificationresponse.notificationCode === "req_asset_donation"
                      ? "finance_estimated_value"
                      : "funds_add_fund_amount",
                    value: isDelete ? (origAmountStr || propAmountStr) : propAmountStr,
                    isBadge: true
                  });
                  if (isDelete || !isDateChanged) topDetails.push({
                    labelKey: "funds_date",
                    value: isDelete ? (origDateStr || propDateStr) : propDateStr
                  });
                  if (isDelete) {
                    if (origMemoStr) topDetails.push({ labelKey: "transactions_memo", value: origMemoStr });
                  } else {
                    if (!isMemoChanged && propMemoStr) topDetails.push({ labelKey: "transactions_memo", value: propMemoStr });
                  }

                  return (
                    <>
                      {topDetails.length > 0 && (
                        <Card style={cardStyle("#3B82F6")} className="mb-3">
                          <CardBody style={{ padding: "16px 20px" }}>
                            <div style={sectionHeaderStyle("#2563EB")}>
                              <i className="fa fa-info-circle" style={{ fontSize: "15px" }}></i>
                              <GetLanguageString props="courses_report_details" />
                            </div>
                            <Row className="gy-2">
                              {topDetails.map((item, idx) => (
                                <Col md key={idx}>
                                  <div style={labelStyle}>
                                    {<GetLanguageString props={item.labelKey} />}
                                  </div>
                                  <div>
                                    {item.isBadge ? (
                                      <div style={amountBadgeProposedStyle}>
                                        {item.value}
                                      </div>
                                    ) : (
                                      <div style={valueStyle}>{item.value}</div>
                                    )}
                                  </div>
                                </Col>
                              ))}
                            </Row>
                          </CardBody>
                        </Card>
                      )}

                      {!isDeleteNotification && (isMemoChanged || isAmountChanged || isDateChanged) && (
                        <Row className="mb-4">
                          <Col md="6">
                            <Card style={cardStyle("#64748B")} className="h-100 mb-0">
                              <CardBody style={{ padding: "20px 24px" }}>
                                <div style={sectionHeaderStyle("#475569")}>
                                  <i className="fa fa-history" style={{ fontSize: "15px" }}></i>
                                  <GetLanguageString props="notification_original_details" />
                                </div>
                                <Row className="gy-3">
                                  {isMemoChanged && (
                                    <Col md="12">
                                      <div style={itemBoxStyle(false)}>
                                        <div style={labelStyle}>
                                          {<GetLanguageString props="transactions_memo" />}
                                        </div>
                                        <div style={valueStyle}>{origMemoStr || "-"}</div>
                                      </div>
                                    </Col>
                                  )}
                                  {isAmountChanged && (
                                    <Col md={isDateChanged ? "6" : "12"}>
                                      <div style={itemBoxStyle(false)}>
                                        <div style={labelStyle}>
                                          {<GetLanguageString props="funds_add_fund_amount" />}
                                        </div>
                                        <div>
                                          <div style={{ ...amountBadgeStyle, backgroundColor: "#F3F4F6", color: "#4B5563", borderColor: "#D1D5DB" }}>
                                            {origAmountStr}
                                          </div>
                                        </div>
                                      </div>
                                    </Col>
                                  )}
                                  {isDateChanged && (
                                    <Col md={isAmountChanged ? "6" : "12"}>
                                      <div style={itemBoxStyle(false)}>
                                        <div style={labelStyle}>
                                          {<GetLanguageString props="funds_date" />}
                                        </div>
                                        <div style={valueStyle}>{origDateStr}</div>
                                      </div>
                                    </Col>
                                  )}
                                </Row>
                              </CardBody>
                            </Card>
                          </Col>

                          <Col md="6">
                            <Card style={cardStyle("#10B981")} className="h-100 mb-0">
                              <CardBody style={{ padding: "20px 24px" }}>
                                <div style={sectionHeaderStyle("#059669")}>
                                  <i className="fa fa-edit" style={{ fontSize: "15px" }}></i>
                                  <GetLanguageString props="notification_proposed_changes" />
                                </div>
                                <Row className="gy-3">
                                  {isMemoChanged && (
                                    <Col md="12">
                                      <div style={itemBoxStyle(true)}>
                                        <div style={labelStyle}>
                                          {<GetLanguageString props="transactions_memo" />}
                                        </div>
                                        <div style={valueStyle}>{propMemoStr || "-"}</div>
                                      </div>
                                    </Col>
                                  )}
                                  {isAmountChanged && (
                                    <Col md={isDateChanged ? "6" : "12"}>
                                      <div style={itemBoxStyle(true)}>
                                        <div style={labelStyle}>
                                          {<GetLanguageString props="funds_add_fund_amount" />}
                                        </div>
                                        <div>
                                          <div style={amountBadgeProposedStyle}>
                                            {propAmountStr}
                                          </div>
                                        </div>
                                      </div>
                                    </Col>
                                  )}
                                  {isDateChanged && (
                                    <Col md={isAmountChanged ? "6" : "12"}>
                                      <div style={itemBoxStyle(true)}>
                                        <div style={labelStyle}>
                                          {<GetLanguageString props="funds_date" />}
                                        </div>
                                        <div style={valueStyle}>{propDateStr}</div>
                                      </div>
                                    </Col>
                                  )}
                                </Row>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                      )}
                    </>
                  );
                })()
              ) : (
                (() => {
                  return (
                    <>
                      <Card style={cardStyle("#3B82F6")}>
                  <CardBody style={{ padding: "20px 24px" }}>
                    <div style={sectionHeaderStyle("#2563EB")}>
                      <i className="fa fa-info-circle" style={{ fontSize: "15px" }}></i>
                      {<GetLanguageString props="courses_report_details" />}
                    </div>
                    <Row className="gy-3">
                      <Col md="6">
                        <div style={labelStyle}>
                          {<GetLanguageString props="transactions_memo" />}
                        </div>
                        <div style={valueStyle}>{notificationresponse.memo}</div>
                      </Col>
                      {notificationresponse.isApproved && (
                        <Col md="6">
                          <div style={labelStyle}>
                            {<GetLanguageString props="funds_receipt_number" />}
                          </div>
                          <div style={valueStyle}>
                            {notificationresponse.recieptNumber || notificationresponse.reference}
                          </div>
                        </Col>
                      )}
                      <Col md="6">
                        <div style={labelStyle}>
                          {<GetLanguageString props="funds_add_fund_amount" />}
                        </div>
                        <div>
                          <div style={amountBadgeStyle}>
                            {new Intl.NumberFormat().format(
                              notificationresponse.amount ||
                              notificationresponse.transferAmount ||
                              0,
                            )}
                          </div>
                        </div>
                      </Col>
                      <Col md="6">
                        <div style={labelStyle}>
                          {<GetLanguageString props="funds_date" />}
                        </div>
                        <div style={valueStyle}>
                          {notificationresponse.date ||
                            notificationresponse.requestDate
                            ? new Date(
                              notificationresponse.date ||
                              notificationresponse.requestDate,
                            ).toLocaleDateString("en-GB")
                            : ""}
                        </div>
                      </Col>
                      {notificationresponse.bankAccountName && (
                        <Col md="6">
                          <div style={labelStyle}>
                            {<GetLanguageString props="fund_bank_account" />}
                          </div>
                          <div style={valueStyle}>{notificationresponse.bankAccountName}</div>
                        </Col>
                      )}
                      {notificationresponse.accountHead && (
                        <Col md="6">
                          <div style={labelStyle}>
                            {<GetLanguageString
                              props={
                                isAssetDonationNotification(notificationresponse.notificationCode)
                                  ? "finance_asset_category"
                                  : "funds_account"
                              }
                            />}
                          </div>
                          <div style={valueStyle}>
                            {decodeHtmlEntities(notificationresponse.accountHead)}
                          </div>
                        </Col>
                      )}
                      {notificationresponse.dimension2Name && (
                        <Col md="6">
                          <div style={labelStyle}>
                            {<GetLanguageString
                              props={
                                isAssetDonationNotification(notificationresponse.notificationCode)
                                  ? "finance_asset"
                                  : "funds_dimension_2"
                              }
                            />}
                          </div>
                          <div style={valueStyle}>
                            {decodeHtmlEntities(notificationresponse.dimension2Name)}
                          </div>
                        </Col>
                      )}
                    </Row>
                  </CardBody>
                </Card>
                    </>
                  );
                })()
              )}

              {/* ORIGINAL ENTRIES (IF ENTRIES EXIST) */}
              {!isDeleteNotification(notificationresponse.notificationCode) && Array.isArray(notificationresponse.original?.entries) && notificationresponse.original.entries.length > 0 && (
                <Card style={cardStyle("#9CA3AF")}>
                  <CardBody style={{ padding: "20px 24px" }}>
                    <div style={sectionHeaderStyle("#4B5563")}>
                      <i className="fa fa-list" style={{ fontSize: "15px" }}></i>
                      <GetLanguageString props="notification_original_entries" />
                    </div>
                    <div className="table-responsive no-drag" style={{ borderRadius: "8px", border: "1px solid #E5E7EB", overflow: "hidden" }}>
                      <table className="table table-hover align-middle mb-0" style={{ fontSize: "13px", borderCollapse: "separate" }}>
                        <thead>
                          <tr style={{ background: "#F9FAFB", borderBottom: "2px solid #E5E7EB" }}>
                            <th style={{ padding: "10px 16px", fontWeight: 600, color: "#4B5563" }}>#</th>
                            <th style={{ padding: "10px 16px", fontWeight: 600, color: "#4B5563" }}>
                              {<GetLanguageString props={entryHeadLabelKey(notificationresponse.notificationCode)} />}
                            </th>
                            {(notificationresponse.original.entries.some((e) => e.dimension2Name) || isAssetDonationNotification(notificationresponse.notificationCode)) && (
                              <th style={{ padding: "10px 16px", fontWeight: 600, color: "#4B5563" }}>
                                <GetLanguageString props={entrySubHeadLabelKey(notificationresponse.notificationCode)} />
                              </th>
                            )}
                            <th style={{ padding: "10px 16px", fontWeight: 600, color: "#4B5563" }}>{<GetLanguageString props="transactions_memo" />}</th>
                            <th className="text-end" style={{ padding: "10px 16px", fontWeight: 600, color: "#4B5563" }}>{<GetLanguageString props="funds_add_fund_amount" />}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {notificationresponse.original.entries.map((entry, index) => (
                            <tr key={index} style={{ borderBottom: "1px solid #F3F4F6" }}>
                              <td style={{ padding: "10px 16px", color: "#6B7280", fontWeight: 500 }}>{index + 1}</td>
                                <td style={{ padding: "10px 16px", color: "#1F2937", fontWeight: 600 }}>
                                  {decodeHtmlEntities(entry.accountHead)}
                                </td>
                                {(notificationresponse.original.entries.some((e) => e.dimension2Name) || isAssetDonationNotification(notificationresponse.notificationCode)) && (
                                  <td style={{ padding: "10px 16px", color: "#1F2937", fontWeight: 600 }}>
                                    {decodeHtmlEntities(
                                      entry.dimension2Name || notificationresponse.original?.dimension2Name
                                    ) || "-"}
                                  </td>
                                )}
                                <td style={{ padding: "10px 16px", color: "#4B5563" }}>{entry.lineMemo || "-"}</td>
                                <td className="text-end" style={{ padding: "10px 16px", color: "#111827", fontWeight: 700, fontSize: "14px" }}>
                                  {new Intl.NumberFormat().format(entry.amount || 0)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardBody>
                  </Card>
                )}

              {/* INCOME / EXPENSE DETAILS TABLE (IF ENTRIES EXIST) */}
              {!isDeleteNotification(notificationresponse.notificationCode) && Array.isArray(notificationresponse.entries) && notificationresponse.entries.length > 0 && (
                  <Card style={cardStyle("#10B981")}>
                    <CardBody style={{ padding: "12px 18px" }}>
                      <div style={sectionHeaderStyle("#059669")}>
                        <i className="fa fa-list" style={{ fontSize: "15px" }}></i>
                        <GetLanguageString
                          props={entryDetailsTitleKey(notificationresponse.notificationCode)}
                        />
                      </div>
                      <div className="table-responsive no-drag" style={{ borderRadius: "8px", border: "1px solid #E5E7EB", overflow: "hidden" }}>
                        <table className="table table-hover align-middle mb-0" style={{ fontSize: "13px", borderCollapse: "separate" }}>
                          <thead>
                            <tr style={{ background: "#F9FAFB", borderBottom: "2px solid #E5E7EB" }}>
                              <th style={{ padding: "6px 12px", fontWeight: 600, color: "#4B5563" }}>#</th>
                              <th style={{ padding: "6px 12px", fontWeight: 600, color: "#4B5563" }}>
                                <GetLanguageString
                                  props={entryHeadLabelKey(notificationresponse.notificationCode)}
                                />
                              </th>
                              {(notificationresponse.entries.some((e) => e.dimension2Name) || isAssetDonationNotification(notificationresponse.notificationCode)) && (
                                <th style={{ padding: "6px 12px", fontWeight: 600, color: "#4B5563" }}>
                                  <GetLanguageString props={entrySubHeadLabelKey(notificationresponse.notificationCode)} />
                                </th>
                              )}
                              <th style={{ padding: "6px 12px", fontWeight: 600, color: "#4B5563" }}>{<GetLanguageString props="transactions_memo" />}</th>
                              <th className="text-end" style={{ padding: "6px 12px", fontWeight: 600, color: "#4B5563" }}>{<GetLanguageString props="funds_add_fund_amount" />}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {notificationresponse.entries.map((entry, index) => (
                              <tr key={index} style={{ borderBottom: "1px solid #F3F4F6" }}>
                                <td style={{ padding: "6px 12px", color: "#6B7280", fontWeight: 500 }}>{index + 1}</td>
                                <td style={{ padding: "6px 12px", color: "#1F2937", fontWeight: 600 }}>
                                  {decodeHtmlEntities(entry.accountHead)}
                                </td>
                                {(notificationresponse.entries.some((e) => e.dimension2Name) || isAssetDonationNotification(notificationresponse.notificationCode)) && (
                                  <td style={{ padding: "6px 12px", color: "#1F2937", fontWeight: 600 }}>
                                    {decodeHtmlEntities(
                                      entry.dimension2Name || notificationresponse.dimension2Name
                                    ) || "-"}
                                  </td>
                                )}
                                <td style={{ padding: "6px 12px", color: "#4B5563" }}>{entry.lineMemo || "-"}</td>
                                <td className="text-end" style={{ padding: "6px 12px", color: "#111827", fontWeight: 700, fontSize: "13px" }}>
                                  {new Intl.NumberFormat().format(entry.amount || 0)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardBody>
                  </Card>
                )}

      {((notificationresponse.isApproved || notificationresponse.is_approved) &&
        ((notificationresponse.fileIds && notificationresponse.fileIds.length > 0) ||
         (notificationresponse.file_ids && notificationresponse.file_ids.length > 0) ||
         (notificationresponse.FileIds && notificationresponse.FileIds.length > 0))) ? (
        <Card style={cardStyle("#0EA5E9")}>
          <CardBody style={{ padding: "16px 20px" }}>
            <div style={sectionHeaderStyle("#0284C7")}>
              <i className="fa fa-paperclip" style={{ fontSize: "15px" }}></i>
              <span><GetLanguageString props="expense_attachments" /> ({(notificationresponse.fileIds || notificationresponse.file_ids || notificationresponse.FileIds).length})</span>
            </div>
            <div className="d-flex flex-column gap-2 mt-2">
              {(notificationresponse.fileIds || notificationresponse.file_ids || notificationresponse.FileIds).map((fId, idx) => {
                const driveItem = (notificationresponse.driveFiles || notificationresponse.drive_files || notificationresponse.DriveFiles || [])[idx];
                const rawName = driveItem?.fileName || driveItem?.FileName || driveItem?.file_name;
                const displayName = rawName ? getFileNameWithoutExtension(rawName) : `Attachment ${idx + 1}`;
                return (
                  <div key={idx} className="d-flex justify-content-between align-items-center p-2 rounded border bg-light">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa fa-file text-muted"></i>
                      <span style={{ fontSize: "13px", fontWeight: 500, color: "#374151" }}>
                        {displayName}
                      </span>
                    </div>
                    <div className="d-flex gap-2">
                      <Button
                        color="white"
                        size="sm"
                        style={{ ...btnStyle, border: "1px solid #D1D5DB" }}
                        onClick={() => handleViewAttachmentFileId(fId)}
                      >
                        <i className="fa fa-eye text-primary"></i>
                        <GetLanguageString props="member_add_rafiq_request_view_document" />
                      </Button>
                      <Button
                        color="white"
                        size="sm"
                        style={{ ...btnStyle, border: "1px solid #D1D5DB" }}
                        onClick={() => handleDownloadAttachmentFileId(fId, idx)}
                      >
                        <i className="fa fa-download text-success"></i>
                        <GetLanguageString props="member_add_rafiq_request_download_document" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardBody>
        </Card>
      ) : ((notificationresponse.driveFiles && notificationresponse.driveFiles.length > 0) ||
        (notificationresponse.drive_files && notificationresponse.drive_files.length > 0) ||
        (notificationresponse.DriveFiles && notificationresponse.DriveFiles.length > 0)) ? (
        <Card style={cardStyle("#0EA5E9")}>
          <CardBody style={{ padding: "16px 20px" }}>
            <div style={sectionHeaderStyle("#0284C7")}>
              <i className="fa fa-paperclip" style={{ fontSize: "15px" }}></i>
              <span><GetLanguageString props="expense_attachments" /> ({(notificationresponse.driveFiles || notificationresponse.drive_files || notificationresponse.DriveFiles).length})</span>
            </div>
            <div className="d-flex flex-column gap-2 mt-2">
              {(notificationresponse.driveFiles || notificationresponse.drive_files || notificationresponse.DriveFiles).map((df, idx) => {
                const rawName = df.fileName || df.FileName || df.file_name;
                const displayName = rawName ? getFileNameWithoutExtension(rawName) : `Attachment ${idx + 1}`;
                return (
                <div key={idx} className="d-flex justify-content-between align-items-center p-2 rounded border bg-light">
                  <div className="d-flex align-items-center gap-2">
                    <i className="fa fa-file text-muted"></i>
                    <span style={{ fontSize: "13px", fontWeight: 500, color: "#374151" }}>
                      {displayName}
                    </span>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      color="white"
                      size="sm"
                      style={{ ...btnStyle, border: "1px solid #D1D5DB" }}
                      onClick={() => (df.webViewLink || df.WebViewLink || df.web_view_link) ? window.open(df.webViewLink || df.WebViewLink || df.web_view_link, "_blank") : downloadFile(df.driveFileId || df.DriveFileId || df.drive_file_id)}
                    >
                      <i className="fa fa-eye text-primary"></i>
                      <GetLanguageString props="member_add_rafiq_request_view_document" />
                    </Button>
                    <Button
                      color="white"
                      size="sm"
                      style={{ ...btnStyle, border: "1px solid #D1D5DB" }}
                      onClick={() => downloadFile(df.driveFileId || df.DriveFileId || df.drive_file_id)}
                    >
                      <i className="fa fa-download text-success"></i>
                      <GetLanguageString props="member_add_rafiq_request_download_document" />
                    </Button>
                  </div>
                </div>
                );
              })}
            </div>
            {((notificationresponse.deletedFileIds && notificationresponse.deletedFileIds.length > 0) ||
              (notificationresponse.deleted_file_ids && notificationresponse.deleted_file_ids.length > 0) ||
              (notificationresponse.deletedDriveFileIds && notificationresponse.deletedDriveFileIds.length > 0) ||
              (notificationresponse.deleted_drive_file_ids && notificationresponse.deleted_drive_file_ids.length > 0)) && (
              <div className="mt-3 pt-3" style={{ borderTop: "1px dashed #EF4444" }}>
                <div style={{ ...sectionHeaderStyle("#DC2626"), fontSize: "13px", marginBottom: "8px" }}>
                  <i className="fa fa-trash text-danger" style={{ fontSize: "14px" }}></i>
                  <span className="text-danger font-weight-bold">
                    <GetLanguageString props="deleted_attachments" /> (
                    {((notificationresponse.deletedFileIds || notificationresponse.deleted_file_ids || []).length) +
                     ((notificationresponse.deletedDriveFileIds || notificationresponse.deleted_drive_file_ids || []).length)}
                    )
                  </span>
                </div>
                <div className="d-flex flex-column gap-2">
                  {(notificationresponse.deletedFileIds || notificationresponse.deleted_file_ids || []).map((fId, idx) => (
                    <div key={`del-f-${idx}`} className="d-flex justify-content-between align-items-center p-2 rounded" style={{ backgroundColor: "#FEF2F2", border: "1px solid #FCA5A5" }}>
                      <div className="d-flex align-items-center gap-2">
                        <i className="fa fa-times-circle text-danger"></i>
                        <span style={{ fontSize: "12px", color: "#991B1B", textDecoration: "line-through" }}>
                          Attachment (Finance File ID: {fId})
                        </span>
                      </div>
                      <span className="badge bg-danger text-white" style={{ fontSize: "11px", padding: "4px 8px" }}>
                        <GetLanguageString props="marked_for_removal" />
                      </span>
                    </div>
                  ))}
                  {(notificationresponse.deletedDriveFileIds || notificationresponse.deleted_drive_file_ids || []).map((dId, idx) => (
                    <div key={`del-d-${idx}`} className="d-flex justify-content-between align-items-center p-2 rounded" style={{ backgroundColor: "#FEF2F2", border: "1px solid #FCA5A5" }}>
                      <div className="d-flex align-items-center gap-2">
                        <i className="fa fa-times-circle text-danger"></i>
                        <span style={{ fontSize: "12px", color: "#991B1B", textDecoration: "line-through" }}>
                          Google Drive File (ID: {dId.substring(0, 8)}...)
                        </span>
                      </div>
                      <span className="badge bg-danger text-white" style={{ fontSize: "11px", padding: "4px 8px" }}>
                        <GetLanguageString props="marked_for_removal" />
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      ) : ((notificationresponse.file ||
        notificationresponse.File ||
        notificationresponse.fileDriveId ||
        notificationresponse.file_drive_id ||
        notificationresponse.approvalFileDriveId ||
        ((notificationresponse.isApproved || notificationresponse.is_approved) &&
          (notificationresponse.transactionId || notificationresponse.transaction_id) &&
          (notificationresponse.fileId || notificationresponse.file_id))) && (
        <Card style={cardStyle("#0EA5E9")}>
          <CardBody className="d-flex justify-content-between align-items-center" style={{ padding: "20px 24px" }}>
            <div>
              <div style={sectionHeaderStyle("#0284C7")}>
                <i className="fa fa-paperclip" style={{ fontSize: "15px" }}></i>
                <GetLanguageString props="attachment" />
              </div>
              <div style={valueStyle}>
                {notificationresponse.notificationCode === "req_expense_from_amanat" ||
                notificationresponse.notificationCode === "req_edit_expense_from_amanat" ||
                notificationresponse.notificationCode === "req_expense_from_cash_in_hand" ||
                notificationresponse.notificationCode === "req_edit_expense_from_cash_in_hand" ||
                notificationresponse.notificationCode === "req_markaz_share" ||
                notificationresponse.notificationCode === "req_edit_markaz_share" ? (
                  notificationresponse.notificationCode === "req_markaz_share" ||
                  notificationresponse.notificationCode === "req_edit_markaz_share" ? (
                    notificationresponse.sourceAccountType === "cash" ||
                    notificationresponse.source_account_type === "cash" ||
                    notificationresponse.accountTypeCode === "cash_in_hand_account" ||
                    (notificationresponse.bankAccountName || "").toLowerCase().includes("cash") ? (
                      <GetLanguageString props="deposit_slip" />
                    ) : (
                      <GetLanguageString props="markaz_share_justification_attachment" />
                    )
                  ) : (
                    "Approval Document"
                  )
                ) : (
                  <GetLanguageString props="halqa_loan_letter" />
                )}
              </div>
            </div>

            <div className="no-drag d-flex gap-2">
              <Button
                color="white"
                disabled={viewButtonDisable}
                size="sm"
                style={{ ...btnStyle, border: "1px solid #D1D5DB" }}
                onClick={() =>
                  openFile(notificationresponse.file || notificationresponse.File)
                }
              >
                {viewButtonDisable ? (
                  <Spinner size="sm" color="primary" />
                ) : (
                  <>
                    <i className="fa fa-eye text-primary"></i>
                    <GetLanguageString props="member_add_rafiq_request_view_document" />
                  </>
                )}
              </Button>
              <Button
                color="white"
                size="sm"
                style={{ ...btnStyle, border: "1px solid #D1D5DB" }}
                disabled={buttonDisable}
                onClick={() =>
                  downloadFile(
                    notificationresponse.fileDriveId ||
                      notificationresponse.file_drive_id ||
                      notificationresponse.approvalFileDriveId,
                  )
                }
              >
                {buttonDisable ? (
                  <Spinner size="sm" color="success" />
                ) : (
                  <>
                    <i className="fa fa-download text-success"></i>
                    <GetLanguageString props="member_add_rafiq_request_download_document" />
                  </>
                )}
              </Button>
            </div>
          </CardBody>
        </Card>
      ))}

              {notificationresponse.historical_summary &&
                notificationresponse.historical_summary.length > 0 && (
                  <Card style={cardStyle("#3B82F6")}>
                    <CardBody style={{ padding: "12px 18px" }}>
                      <div style={sectionHeaderStyle("#2563EB")}>
                        <i className="fa fa-history" style={{ fontSize: "15px" }}></i>
                        <span>{<GetLanguageString props="historical_3_month_summary" />}</span>
                      </div>
                      <div className="table-responsive no-drag" style={{ borderRadius: "8px", border: "1px solid #E5E7EB", overflow: "hidden" }}>
                        <table className="table table-hover align-middle mb-0" style={{ fontSize: "13px", borderCollapse: "separate" }}>
                          <thead>
                            <tr style={{ background: "#F9FAFB", borderBottom: "2px solid #E5E7EB" }}>
                              <th style={{ padding: "6px 12px", fontWeight: 600, color: "#4B5563" }}>{<GetLanguageString props="month_year" />}</th>
                              <th className="text-end" style={{ padding: "6px 12px", fontWeight: 600, color: "#4B5563" }}>{<GetLanguageString props="total_income" />}</th>
                              <th className="text-end" style={{ padding: "6px 12px", fontWeight: 600, color: "#4B5563" }}>{<GetLanguageString props="total_expense" />}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {notificationresponse.historical_summary.map((summary, index) => (
                              <tr key={index} style={{ borderBottom: "1px solid #F3F4F6" }}>
                                <td style={{ padding: "6px 12px", color: "#1F2937", fontWeight: 600 }}>{summary.period}</td>
                                <td className="text-end" style={{ padding: "6px 12px", color: "#059669", fontWeight: 700 }}>
                                  {new Intl.NumberFormat().format(summary.total_income || 0)}
                                </td>
                                <td className="text-end" style={{ padding: "6px 12px", color: "#DC2626", fontWeight: 700 }}>
                                  {new Intl.NumberFormat().format(summary.total_expense || 0)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardBody>
                  </Card>
                )}

              {(notificationresponse.rafiqAccountTitle ||
                notificationresponse.rafiqAccountNumber ||
                notificationresponse.rafiqBankName ||
                notificationresponse.vendorName) ? (
                <Card style={cardStyle("#8B5CF6")}>
                  <CardBody style={{ padding: "12px 18px" }}>
                    <div style={sectionHeaderStyle("#7C3AED")}>
                      <i className="fa fa-university" style={{ fontSize: "15px" }}></i>
                      <GetLanguageString props="transactions_beneficiary_details" />
                    </div>

                    <Row className="gy-2">
                      {/* Subheading: Vendor Info */}
                      {(notificationresponse.vendorName || notificationresponse.ntnNumber) && (
                        <>
                          <Col md="12">
                            <h6 style={{ fontWeight: 600, color: "#4B5563", fontSize: "11px", borderBottom: "1px dashed #D1D5DB", paddingBottom: "3px", marginBottom: "6px" }}>
                              <GetLanguageString props="vendor_details" />
                            </h6>
                          </Col>

                          <Col md="4">
                            <div style={labelStyle}>
                              <GetLanguageString props="vendor_entity_type" />
                            </div>
                            <div style={valueStyle}>
                              <span style={{ fontSize: "11px", fontWeight: 600, background: (notificationresponse.vendorType === "Company/Organization" || notificationresponse.ntnNumber) ? "#E0E7FF" : "#FEF3C7", color: (notificationresponse.vendorType === "Company/Organization" || notificationresponse.ntnNumber) ? "#3730A3" : "#92400E", padding: "2px 8px", borderRadius: "10px" }}>
                                {notificationresponse.vendorType === "Company/Organization" || notificationresponse.ntnNumber
                                  ? <><GetLanguageString props="vendor_type_company" /> (NTN)</>
                                  : <><GetLanguageString props="vendor_type_individual" /> (CNIC)</>}
                              </span>
                            </div>
                          </Col>

                          {notificationresponse.vendorName && (
                            <Col md="4">
                              <div style={labelStyle}>
                                <GetLanguageString props="transactions_vendor_name" />
                              </div>
                              <div style={valueStyle}>
                                {applyClassToCharacters(notificationresponse.vendorName, "ur")}
                              </div>
                            </Col>
                          )}

                          {notificationresponse.ntnNumber ? (
                            <Col md="4">
                              <div style={labelStyle}>
                                <GetLanguageString props="transactions_vendor_ntn" />
                              </div>
                              <div style={valueStyle}>
                                {notificationresponse.ntnNumber}
                              </div>
                            </Col>
                          ) : notificationresponse.cnic ? (
                            <Col md="4">
                              <div style={labelStyle}>
                                <GetLanguageString props="transactions_vendor_cnic" />
                              </div>
                              <div style={valueStyle}>
                                {applyClassToCharacters(notificationresponse.cnic, "ur")}
                              </div>
                            </Col>
                          ) : null}

                          <Col md="4">
                            <div style={labelStyle}>
                              <GetLanguageString props="tax_status" />
                            </div>
                            <div style={valueStyle}>
                              <span style={{ fontSize: "11px", fontWeight: 600, background: (notificationresponse.isFiler || notificationresponse.taxStatus === "Filer") ? "#D1FAE5" : "#FEF3C7", color: (notificationresponse.isFiler || notificationresponse.taxStatus === "Filer") ? "#065F46" : "#92400E", padding: "2px 8px", borderRadius: "10px" }}>
                                {notificationresponse.isFiler || notificationresponse.taxStatus === "Filer" ? <GetLanguageString props="tax_status_filer" /> : <GetLanguageString props="tax_status_non_filer" />}
                              </span>
                            </div>
                          </Col>
                        </>
                      )}

                      <Col md="12" style={{ marginTop: (notificationresponse.vendorName || notificationresponse.ntnNumber) ? "8px" : "0px" }}>
                        <h6 style={{ fontWeight: 600, color: "#4B5563", fontSize: "11px", borderBottom: "1px dashed #D1D5DB", paddingBottom: "3px", marginBottom: "6px" }}>
                          <GetLanguageString props="bank_account_details" />
                        </h6>
                      </Col>

                      <Col md="4">
                        <div style={labelStyle}>
                          <GetLanguageString props="transactions_account_title" />
                        </div>
                        <div style={valueStyle}>
                          {applyClassToCharacters((notificationresponse.rafiqAccountTitle || "").split("-")[0].trim() || notificationresponse.rafiqAccountTitle, "ur")}
                        </div>
                      </Col>

                      <Col md="4">
                        <div style={labelStyle}>
                          <GetLanguageString props="transactions_account_number" />
                        </div>
                        <div style={valueStyle}>
                          {applyClassToCharacters(notificationresponse.rafiqAccountNumber, "ur")}
                        </div>
                      </Col>

                      <Col md="4">
                        <div style={labelStyle}>
                          <GetLanguageString props="transactions_bank_name" />
                        </div>
                        <div style={valueStyle}>
                          {applyClassToCharacters(notificationresponse.rafiqBankName, "ur")}
                        </div>
                      </Col>

                      {!notificationresponse.vendorName && notificationresponse.cnic && (
                        <Col md="4">
                          <div style={labelStyle}>
                            <GetLanguageString props="member_cnic" />
                          </div>
                          <div style={valueStyle}>
                            {applyClassToCharacters(
                              (() => {
                                const rawCnic = String(notificationresponse.cnic).replace(/\D/g, "");
                                return rawCnic.length === 13
                                  ? `${rawCnic.slice(0, 5)}-${rawCnic.slice(5, 12)}-${rawCnic.slice(12)}`
                                  : notificationresponse.cnic;
                              })(),
                              "ur"
                            )}
                          </div>
                        </Col>
                      )}
                    </Row>
                  </CardBody>
                </Card>
              ) : null}
            </ModalBody>
          )}

          <ModalFooter>
            <Button
              color="primary"
              style={{ borderRadius: "8px", padding: "8px 24px", fontWeight: 600 }}
              onClick={toggle}
            >
              <GetLanguageString props="users_role_ok_btn" />
            </Button>
          </ModalFooter>
        </Modal>
      </Draggable>
    </>
  );
};

export default FundsOtherIncomeNotificationDetails;
